import Vue from 'vue'
import { mapGetters } from 'vuex'
import PaidLabel from '@/components/PaidLabel/PaidLabel.vue'
import ButtonColumn from '@/components/Columns/ButtonColumn.vue'
import DurationColumn from '@/components/Columns/DurationColumn.vue'
import MenuColumn from '@/components/Columns/MenuColumn.vue'
import NumberColumn from '@/components/Columns/NumberColumn.vue'
import StatusColumn from '@/components/Columns/StatusColumn.vue'
import StringColumn from '@/components/Columns/StringColumn.vue'
import ScriptRowTests from '@/components/Scripts/ScriptRowTests.vue'
import ScriptRowVersions from '@/components/Scripts/ScriptRowVersions.vue'
import {
  ScriptColumnInitObject,
  ScriptInstance,
  ScriptUnit,
} from '@/models/scripts'
import { SCRIPTS__FETCH_ONE } from '@/store/actions'
import { UserScope } from '@/models/users'
import { GridColumn } from '@/@types'

interface Data {
  isFetching: boolean
  showTests: boolean
  showVersions: boolean
}

interface Methods {
  getComponentForThis: (column: GridColumn) => string
  getInitObjectForThis: (column?: GridColumn) => ScriptColumnInitObject
  toggleVersions: () => void
  toggleTests: () => void
}

interface Computed {
  canDoScope: (checkingScope: UserScope) => boolean
  draftId?: string
  hasTests: boolean
  hasVersions: boolean
  canViewTests: boolean
  canViewVersions: boolean
  canViewStatistic: boolean
  scriptItemClasses: object
}

interface Props {
  item: ScriptUnit
  columns: GridColumn[]
  columnsWidth: string
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    PaidLabel,
    ButtonColumn,
    DurationColumn,
    MenuColumn,
    NumberColumn,
    StatusColumn,
    StringColumn,
    ScriptRowTests,
    ScriptRowVersions,
  },
  props: {
    columns: {
      required: true,
    },
    columnsWidth: {
      required: true,
    },
    item: {
      required: true,
    },
  },
  data: () => ({
    isFetching: false,
    showTests: false,
    showVersions: false,
  }),
  computed: {
    ...mapGetters(['canDoScope']),
    draftId(): string | undefined {
      return this.item.versions?.find(v => !v.instance.date)?.instance.id
    },
    hasTests(): boolean {
      return !!this.item.tests?.length
    },
    hasVersions(): boolean {
      return !!this.item.versions?.length
    },
    canViewTests(): boolean {
      return this.hasTests && this.canDoScope(UserScope.ScriptsEdit)
    },
    canViewVersions(): boolean {
      return this.hasVersions && this.canDoScope(UserScope.ScriptsPublish)
    },
    canViewStatistic(): boolean {
      return this.canDoScope(UserScope.ScriptsEdit)
    },
    scriptItemClasses(): object {
      return {
        '-has-tests': this.canViewTests,
        '-has-versions': this.canViewVersions,
        '-show-tests': this.showTests,
        '-show-versions': this.showVersions,
      }
    },
  },

  methods: {
    getComponentForThis(column: GridColumn): string {
      return `${column.type}Column`
    },
    getInitObjectForThis(column?: GridColumn): ScriptColumnInitObject {
      const init: ScriptColumnInitObject = {
        id: this.item.id,
        title: this.item.title,
        value: null,
      }
      const instance: ScriptInstance = this.item.instance as ScriptInstance
      const hasInstance = !!instance.id
      if (hasInstance) {
        init.instanceId = instance.id
      }
      if (!column) {
        return init
      }
      switch (column.name) {
        case 'status':
          init.value = this.item.status
          break
        case 'title':
          init.value = this.item.title
          break
        case 'steps':
          init.value = hasInstance ? instance.steps : null
          break
        case 'pass':
          init.value = hasInstance ? instance.pass : null
          break
        case 'target':
          init.value = hasInstance ? instance.target : null
          break
        case 'conversion':
          if (hasInstance) {
            init.target = instance.target
            init.pass = instance.pass
            if (init.pass > 0) {
              init.value =
                new Intl.NumberFormat('ru-RU', {
                  maximumFractionDigits: 2,
                }).format((init.target / init.pass) * 100) + '%'
            } else {
              init.value = '0%'
            }
          }
          break
        case 'duration':
          init.value = hasInstance ? instance.duration : null
          break
        case 'menu':
          init.draftId = this.draftId
          init.versionsCount = this.item.versions?.length || 0
        // eslint-disable-next-line no-fallthrough
        case 'id':
          init.isShow = true
        // eslint-disable-next-line no-fallthrough
        case 'buttons':
          init.value = this.item.id
          init.status = this.item.status
          init.draftId = this.draftId
          init.hasVersions = this.hasVersions
          init.versionsCount = this.item.versions?.length || 0
      }
      return init
    },
    toggleVersions(): void {
      this.showTests = false
      if (!this.showVersions && !this.isFetching) {
        this.isFetching = true
        this.$store
          .dispatch(SCRIPTS__FETCH_ONE, {
            id: this.item.id,
          })
          .finally(() => {
            this.isFetching = false
          })
      }
      this.showVersions = !this.showVersions
    },
    toggleTests(): void {
      this.showVersions = false
      if (!this.showTests && !this.isFetching) {
        this.isFetching = true
        this.$store
          .dispatch(SCRIPTS__FETCH_ONE, {
            id: this.item.id,
          })
          .finally(() => {
            this.isFetching = false
          })
      }
      this.showTests = !this.showTests
    },
  },
  watch: {
    item(val: ScriptUnit) {
      if (!val.versions?.length) {
        this.showVersions = false
      }
    },
  },
})
